package com.islamicmedia.shared.data.api

import com.islamicmedia.shared.domain.model.BookMeta
import com.islamicmedia.shared.util.IsbnUtils
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.contentType
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.withTimeoutOrNull
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive

/**
 * Resolves an ISBN to book metadata so a scan can skip the disambiguation screen.
 *
 * Google Books is primary (cleaner titles, real author names, reliable cover
 * thumbnails — best for the confirm card); Open Library is the fallback for the
 * long tail Google misses. Both are queried by the canonical ISBN-13 so a
 * paperback ISBN-10 and its ISBN-13 resolve to the same key.
 */
class IsbnLookupService(
    private val client: HttpClient
) {
    private val json = Json { ignoreUnknownKeys = true; isLenient = true }

    suspend fun resolve(rawIsbn: String): BookMeta? {
        val isbn13 = IsbnUtils.toIsbn13(rawIsbn) ?: return null
        // Tiers 1+2 are the structured catalogs — both free and fast, so fire them concurrently and
        // prefer Google Books' richer result (cleaner titles + reliable cover), falling back to Open
        // Library. Only if BOTH miss do we pay for tier 3 (web research via our server: slower,
        // costs a Perplexity call, title-only) — the safety net for editions the catalogs haven't
        // indexed. Worst case is two round trips (parallel catalogs → research), not three.
        val (fromGoogle, fromOpenLib) = coroutineScope {
            val g = async { runCatching { fromGoogleBooks(isbn13) }.getOrNull() }
            val o = async { runCatching { fromOpenLibrary(isbn13) }.getOrNull() }
            g.await() to o.await()
        }
        val meta = fromGoogle
            ?: fromOpenLib
            ?: runCatching { fromWebResearch(isbn13) }.getOrNull()
            ?: return null
        // Warm the cover during the lookup wait the user is already enduring, so
        // the confirm card shows the image instantly. Time-boxed: on a slow link
        // we return without bytes and the UI falls back to a spinner + URL load.
        val bytes = meta.coverUrl?.let { url ->
            withTimeoutOrNull(1500) {
                runCatching { client.get(url).body<ByteArray>() }.getOrNull()
            }
        }
        return if (bytes != null) meta.copy(coverBytes = bytes) else meta
    }

    private suspend fun fromGoogleBooks(isbn13: String): BookMeta? {
        val text = client
            .get("https://www.googleapis.com/books/v1/volumes?q=isbn:$isbn13")
            .bodyAsText()
        val root = json.parseToJsonElement(text).jsonObject
        val items = root["items"]?.jsonArray ?: return null
        val info = items.firstOrNull()?.jsonObject?.get("volumeInfo")?.jsonObject ?: return null

        val title = info["title"]?.jsonPrimitive?.contentOrNull?.takeIf { it.isNotBlank() }
            ?: return null
        val subtitle = info["subtitle"]?.jsonPrimitive?.contentOrNull
        val authors = info["authors"]?.jsonArray
            ?.mapNotNull { it.jsonPrimitive.contentOrNull }
            ?: emptyList()
        val year = info["publishedDate"]?.jsonPrimitive?.contentOrNull?.let { extractYear(it) }
        val cover = info["imageLinks"]?.jsonObject?.let {
            (it["thumbnail"] ?: it["smallThumbnail"])?.jsonPrimitive?.contentOrNull
        }?.replace("http://", "https://")

        return BookMeta(
            isbn13 = isbn13,
            title = if (subtitle.isNullOrBlank()) title else "$title: $subtitle",
            authors = authors,
            year = year,
            coverUrl = cover
        )
    }

    private suspend fun fromOpenLibrary(isbn13: String): BookMeta? {
        val text = client
            .get("https://openlibrary.org/api/books?bibkeys=ISBN:$isbn13&format=json&jscmd=data")
            .bodyAsText()
        val root = json.parseToJsonElement(text).jsonObject
        val entry = (root["ISBN:$isbn13"] as? JsonObject) ?: return null

        val title = entry["title"]?.jsonPrimitive?.contentOrNull?.takeIf { it.isNotBlank() }
            ?: return null
        val authors = entry["authors"]?.jsonArray
            ?.mapNotNull { it.jsonObject["name"]?.jsonPrimitive?.contentOrNull }
            ?: emptyList()
        val year = entry["publish_date"]?.jsonPrimitive?.contentOrNull?.let { extractYear(it) }
        // Prefer the medium cover: ~180px is plenty for the ~116dp confirm card
        // and a fraction of the weight of Open Library's "large" (often 500KB+).
        val cover = entry["cover"]?.jsonObject?.let {
            (it["medium"] ?: it["small"] ?: it["large"])?.jsonPrimitive?.contentOrNull
        }

        return BookMeta(
            isbn13 = isbn13,
            title = title,
            authors = authors,
            year = year,
            coverUrl = cover
        )
    }

    /**
     * Tier 3: our server resolves the ISBN via Perplexity web search when both catalogs miss.
     * Returns a title (and author/year when known) but no cover — the confirm card shows a
     * placeholder and the analysis backfills cover art later.
     */
    private suspend fun fromWebResearch(isbn13: String): BookMeta? {
        val text = client
            .post("${serverBaseUrl()}/api/isbn-lookup") {
                contentType(ContentType.Application.Json)
                setBody("""{"isbn":"$isbn13"}""")
            }
            .bodyAsText()
        val root = json.parseToJsonElement(text).jsonObject
        val title = root["title"]?.jsonPrimitive?.contentOrNull?.takeIf { it.isNotBlank() }
            ?: return null
        val authors = root["authors"]?.jsonArray
            ?.mapNotNull { it.jsonPrimitive.contentOrNull }
            ?: emptyList()
        val year = root["year"]?.jsonPrimitive?.contentOrNull

        return BookMeta(
            isbn13 = isbn13,
            title = title,
            authors = authors,
            year = year,
            coverUrl = null
        )
    }

    /** Pulls a 4-digit year out of "2004", "2004-05-01", "May 2004", etc. */
    private fun extractYear(raw: String): String? =
        Regex("""(1[5-9]\d{2}|20\d{2})""").find(raw)?.value
}
